import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Target, TrendingUp, Users, Calendar, Plus, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  created_by: string | null;
  status: 'pending' | 'completed';
  completed_at: string | null;
  created_at: string;
}

export default function SchoolLiaisonDashboard() {
  const { toast } = useToast();
  const [goals, setGoals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);

  useEffect(() => {
    fetchGoals();
    fetchTasks();
  }, []);

  const fetchGoals = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from("goals")
      .select("*")
      .eq("pta_id", memberData.pta_id)
      .eq("status", "approved")
      .order("priority", { ascending: false });
    
    setGoals(data || []);
    setLoading(false);
  };

  const fetchTasks = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .eq('assigned_to', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as Task[]);
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { error } = await supabase
      .from('tasks')
      .insert([{
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'personal',
        assigned_to: user.id,
        created_by: user.id,
        status: 'pending',
        pta_id: memberData.pta_id
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "Task added", description: "Your task has been created successfully." });
      setNewTaskTitle("");
      setNewTaskDescription("");
      setIsTaskDialogOpen(false);
      fetchTasks();
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null
      })
      .eq('id', taskId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Task deleted", description: "The task has been removed." });
      fetchTasks();
    }
  };

  const calculateProgress = (current: number, target: number) => {
    return Math.min(Math.round((current / target) * 100), 100);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">School Liaison Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          View PTA goals and coordinate with school leadership
        </p>
      </div>

      <Tabs defaultValue="goals" className="w-full">
        <TabsList>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="goals" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Goals</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{goals.length}</div>
                <p className="text-xs text-muted-foreground">Active fundraising goals</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Target</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  £{goals.reduce((sum, g) => sum + (Number(g.target_amount) || 0), 0).toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Combined goal amount</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Raised</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  £{goals.reduce((sum, g) => sum + (Number(g.current_amount) || 0), 0).toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Current progress</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Progress</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {goals.length > 0
                    ? Math.round(
                        goals.reduce(
                          (sum, g) => sum + calculateProgress(g.current_amount, g.target_amount),
                          0
                        ) / goals.length
                      )
                    : 0}
                  %
                </div>
                <p className="text-xs text-muted-foreground">Overall completion</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Active Goals</h3>
            {loading ? (
              <Card>
                <CardContent className="p-8">
                  <p className="text-center text-muted-foreground">Loading goals...</p>
                </CardContent>
              </Card>
            ) : goals.length > 0 ? (
              goals.map((goal) => (
                <Card key={goal.id} className="shadow-card">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{goal.title}</CardTitle>
                        {goal.details && (
                          <CardDescription>{goal.details}</CardDescription>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={goal.priority > 5 ? "default" : "secondary"}>
                          Priority: {goal.priority}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {goal.status}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">
                          £{Number(goal.current_amount).toLocaleString()} / £
                          {Number(goal.target_amount).toLocaleString()}
                        </span>
                      </div>
                      <Progress
                        value={calculateProgress(goal.current_amount, goal.target_amount)}
                        className="h-2"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {calculateProgress(goal.current_amount, goal.target_amount)}% complete
                      </p>
                    </div>
                    {goal.target_date && (
                      <p className="text-sm text-muted-foreground">
                        Target date: {new Date(goal.target_date).toLocaleDateString()}
                      </p>
                    )}
                    {goal.requester && (
                      <p className="text-sm text-muted-foreground">
                        Requested by: {goal.requester}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8">
                  <p className="text-center text-muted-foreground">
                    No active goals at the moment. Check back later.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>My Tasks</CardTitle>
                  <CardDescription>Manage your personal tasks and view assigned tasks</CardDescription>
                </div>
                <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Task</DialogTitle>
                      <DialogDescription>Add a personal task to your list</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddTask} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="task-title">Task Title *</Label>
                        <Input
                          id="task-title"
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          placeholder="Enter task title"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="task-description">Description</Label>
                        <Textarea
                          id="task-description"
                          value={newTaskDescription}
                          onChange={(e) => setNewTaskDescription(e.target.value)}
                          placeholder="Enter task description (optional)"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Add Task</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No tasks yet. Add a task to get started!
                </p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => handleToggleTask(task.id, task.status)}
                        className="mt-1"
                      >
                        {task.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                      <div className="flex-1">
                        <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </h4>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={task.task_type === 'personal' ? 'default' : 'secondary'}>
                            {task.task_type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(task.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {task.task_type === 'personal' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
